import type { GameState } from './physics';
import type { PhysicsEvent, Role } from '../types';

/**
 * Right-side stats panel. Owns nothing of its own visually — just reads
 * the static stats-row elements from the DOM and updates their values
 * each tick / on events. Keep this lightweight: the UI repaints at
 * 60Hz, so each update is one textContent write per stat at most.
 */
export interface Stats {
  tick(state: GameState, now: number): void;
  applyEvents(events: readonly PhysicsEvent[]): void;
  reset(): void;
}

export function createStats(role: Role): Stats {
  const timeEl = byId('stat-time');
  const hitsEl = byId('stat-hits');
  const wallsEl = byId('stat-walls');
  const peakEl = byId('stat-peak');

  let startedAt = 0;
  let hits = 0;
  let walls = 0;
  let peak = 0;
  let lastTimeText = '';

  const reset = (): void => {
    startedAt = 0;
    hits = 0;
    walls = 0;
    peak = 0;
    lastTimeText = '';
    timeEl.textContent = '0:00';
    hitsEl.textContent = '0';
    wallsEl.textContent = '0';
    peakEl.textContent = '0.0';
  };

  reset();

  return {
    tick(state, now) {
      // Start the wall clock on first tick. Includes the brief serve
      // pause; close enough to match-elapsed for the player.
      if (startedAt === 0) startedAt = now;

      const speed = Math.hypot(state.puck.vel.x, state.puck.vel.y);
      if (speed > peak) {
        peak = speed;
        peakEl.textContent = peak.toFixed(1);
      }

      const elapsed = now - startedAt;
      const mins = Math.floor(elapsed / 60000);
      const secs = Math.floor((elapsed % 60000) / 1000);
      const txt = `${mins}:${secs < 10 ? '0' : ''}${secs}`;
      if (txt !== lastTimeText) {
        timeEl.textContent = txt;
        lastTimeText = txt;
      }
    },
    applyEvents(events) {
      let changed = false;
      let wallsChanged = false;
      for (const e of events) {
        if (e.e === 'hit' && e.paddle === role) {
          hits++;
          changed = true;
        } else if (e.e === 'wall') {
          walls++;
          wallsChanged = true;
        }
      }
      if (changed) hitsEl.textContent = String(hits);
      if (wallsChanged) wallsEl.textContent = String(walls);
    },
    reset,
  };
}

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el;
}
